type Cleanup = () => void

export interface ListenerScope {
  onWindow: <K extends keyof WindowEventMap>(
    event: K,
    handler: (ev: WindowEventMap[K]) => void,
    options?: AddEventListenerOptions,
  ) => Cleanup
  onDocument: <K extends keyof DocumentEventMap>(
    event: K,
    handler: (ev: DocumentEventMap[K]) => void,
    options?: AddEventListenerOptions,
  ) => Cleanup
  setInterval: (handler: () => void, ms: number) => Cleanup
  requestAnimationFrame: (handler: FrameRequestCallback) => Cleanup
  dispose: () => void
}

export function createListenerScope(): ListenerScope {
  const cleanups: Cleanup[] = []

  const addCleanup = (cleanup: Cleanup) => {
    cleanups.push(cleanup)
    return cleanup
  }

  return {
    onWindow: (event, handler, options) => {
      if (typeof window === 'undefined') return () => {}
      window.addEventListener(event, handler, options)
      return addCleanup(() => window.removeEventListener(event, handler, options))
    },
    onDocument: (event, handler, options) => {
      if (typeof document === 'undefined') return () => {}
      document.addEventListener(event, handler, options)
      return addCleanup(() => document.removeEventListener(event, handler, options))
    },
    setInterval: (handler, ms) => {
      if (typeof window === 'undefined') return () => {}
      const id = window.setInterval(handler, ms)
      return addCleanup(() => window.clearInterval(id))
    },
    requestAnimationFrame: (handler) => {
      if (typeof window === 'undefined') return () => {}
      const id = window.requestAnimationFrame(handler)
      return addCleanup(() => window.cancelAnimationFrame(id))
    },
    dispose: () => {
      for (let i = cleanups.length - 1; i >= 0; i--) {
        cleanups[i]()
      }
      cleanups.length = 0
    },
  }
}
